import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useHistory, useParams, useRouteMatch } from "react-router-dom";
import routes from "routes/routes";
import { useAppSelector } from "redux/hooks";
import { selectUser } from "module/auth/redux/authSlice";
import { toastWarning } from "layout/toast/helper";
import t from "module/translations";
import EntitySignContextProvider from "module/entity/context/sign/EntitySignContextProvider";
import { EntityIdentifier, EntityType } from "module/entity/types";
import { CertificationInterface } from "module/certification/types";
import { getPathToCertificationToBeSignedDetailsOverview } from "module/certification/helper/getPath";
import getCertificationDetails from "api/competencyManagement/getCertificationDetails";
import { canSignCertification } from "module/certification/helper/canSignCertification";
import CertificationContext from "module/certification/context/CertificationContext";
import CertificationSignContext from "module/certification/sign/CertificationSignContext";
import CertificationToBeSignedDetailsContainer from "./CertificationToBeSignedDetailsContainer";

const CertificationToBeSignedDetailsPage: React.FC = () => {
  const { certificationId: id } = useParams<{ certificationId: string }>();
  const [certification, setCertification] =
    useState<CertificationInterface | null>(null);
  const history = useHistory();
  const match = useRouteMatch({
    path: routes.certificationToBeSignedDetails.path,
    exact: true,
  });
  const loggedInStaffId = useAppSelector(selectUser).id;

  useEffect(() => {
    if (id && match) {
      history.push(getPathToCertificationToBeSignedDetailsOverview(id));
    }
  }, [history, id, match]);

  const load = useCallback(async () => {
    const myCertification = await getCertificationDetails(Number(id));
    setCertification(myCertification.data);
  }, [id]);

  useEffect(() => {
    if (
      certification != null &&
      !canSignCertification({ certification, loggedInStaffId })
    ) {
      toastWarning(t.toBeSigned.itemNoAbleToSignText);
      history.push(routes.toBeSigned.path);
    }
  }, [certification, history, loggedInStaffId]);

  useEffect(() => {
    load();
  }, [load]);

  const entityIdentifier: EntityIdentifier = useMemo(
    () => ({
      id,
      type: EntityType.certification,
      backendId: Number(id),
    }),
    [id]
  );

  if (certification == null) {
    return null;
  }

  return (
    <CertificationContext.Provider value={{ entity: certification }}>
      <EntitySignContextProvider
        {...{ entityIdentifier, context: CertificationSignContext }}
      >
        <CertificationToBeSignedDetailsContainer />
      </EntitySignContextProvider>
    </CertificationContext.Provider>
  );
};

export default React.memo(CertificationToBeSignedDetailsPage);
